import React, { useCallback, useMemo, useState } from "react";
import {
  Badge,
  ExpandableSection,
  Header,
  SpaceBetween,
} from "@amzn/awsui-components-react";
import CbeResults from "./CbeResults";
import CbeHealthStatusDisplay from "./CbeHealthStatusDisplay";
import { CanonicalBusinessEvent } from "../../types";
import VersionSelector from "../common/VersionSelector";
import { groupCbesByProductIds } from "../../helpers/bundleUtils";
import { BADGE_COLORS } from "../../helpers/errorUtils";

interface Props {
  cbeData: CanonicalBusinessEvent[];
  onVersionChange: (index: number) => void;
  allProductIds: string[];
  isBundle: boolean;
  onProductsToggle?: (toggleFunction: () => void) => void;
}

export default function CbeVersionSelector({
  cbeData,
  onVersionChange,
  allProductIds,
  isBundle,
  onProductsToggle,
}: Props) {
  const [expandedGroups, setExpandedGroups] = useState<Record<string, boolean>>(
    {}
  );
  const [allProductsExpanded, setAllProductsExpanded] = useState(true);

  const productGroups = useMemo(() => {
    if (!isBundle) {
      return [];
    }

    const groups = groupCbesByProductIds(cbeData, allProductIds);

    // If no groups found, fall back to non-bundle rendering
    if (groups.size === 0) {
      return [];
    }

    return Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([productId, cbes]) => ({
        productId,
        cbes,
      }));
  }, [cbeData, allProductIds, isBundle]);

  const toggleAllProducts = useCallback(() => {
    const newState = !allProductsExpanded;
    setAllProductsExpanded(newState);

    const newExpandedGroups = productGroups.reduce((acc, group) => {
      acc[group.productId] = newState;
      return acc;
    }, {} as Record<string, boolean>);
    setExpandedGroups(newExpandedGroups);
  }, [allProductsExpanded, productGroups]);

  // Call onProductsToggle only when we have multiple products
  React.useEffect(() => {
    if (onProductsToggle && isBundle && productGroups.length > 1) {
      onProductsToggle(toggleAllProducts);
    }
  }, [isBundle, productGroups.length, toggleAllProducts, onProductsToggle]);

  const handleBundleVersionChange = useCallback(
    (groupIndex: number) => (relativeIndex: number) => {
      let absoluteIndex = 0;
      for (let i = 0; i < groupIndex; i += 1) {
        absoluteIndex += productGroups[i].cbes.length;
      }
      onVersionChange(absoluteIndex + relativeIndex);
    },
    [productGroups, onVersionChange]
  );

  const renderTabLabel = useCallback(
    (version: number, cbesForVersion: CanonicalBusinessEvent[]) => (
      <CbeHealthStatusDisplay
        name="Version"
        events={cbesForVersion}
        value={version.toString()}
      />
    ),
    []
  );

  const renderTabContent = useCallback(
    (cbesForVersion: CanonicalBusinessEvent[]) => (
      <CbeResults cbeData={cbesForVersion} />
    ),
    []
  );

  if (productGroups.length === 0) {
    // Fallback for non-bundles or no products.
    return (
      <VersionSelector
        data={cbeData}
        onVersionChange={onVersionChange}
        getVersion={(cbe) => cbe.lockVersion}
        renderTabLabel={renderTabLabel}
        renderTabContent={renderTabContent}
      />
    );
  }

  return (
    <SpaceBetween size="l">
      {productGroups.map((group, index) => (
        <ExpandableSection
          key={group.productId}
          variant="container"
          expanded={expandedGroups[group.productId] ?? true}
          onChange={(event) =>
            setExpandedGroups((prev) => ({
              ...prev,
              [group.productId]: event.detail.expanded,
            }))
          }
          header={
            <Header
              variant="h3"
              description={
                <small>
                  {group.cbes
                    .map((cbe) => cbe.usecaseName)
                    .filter(
                      (name, idx, arr) => name && arr.indexOf(name) === idx
                    )
                    .join(", ") || ""}
                </small>
              }
            >
              <SpaceBetween
                direction="horizontal"
                size="xs"
                alignItems="center"
              >
                <Badge color={BADGE_COLORS[index % BADGE_COLORS.length]}>
                  {index + 1}
                </Badge>
                Product: {group.productId}
              </SpaceBetween>
            </Header>
          }
        >
          <VersionSelector
            data={group.cbes}
            onVersionChange={handleBundleVersionChange(index)}
            getVersion={(cbe) => cbe.lockVersion}
            renderTabLabel={renderTabLabel}
            renderTabContent={renderTabContent}
          />
        </ExpandableSection>
      ))}
    </SpaceBetween>
  );
}

CbeVersionSelector.defaultProps = {
  onProductsToggle: undefined,
};
